import React from "react";
import AppButton from "./AppButton";

/**
 * IconButton — a button whose whole content is an icon.
 *
 * It renders `AppButton`, so there is still one button implementation: variants,
 * hover, focus ring and disabled state all come from there. It adds the two
 * things an icon-only button needs and a general button cannot enforce.
 *
 *  1. **`label` is required.** An icon-only `<button>` with no text announces as
 *     "button" and nothing else. `label` sets both `aria-label` and `title`, so
 *     the mouse hint and the announced name cannot drift apart. `title` alone is
 *     not enough — screen readers are not required to read it.
 *
 *  2. **The size prop owns the glyph size.** Pass the component (`icon={X}`) and
 *     the icon is sized here; pass children only when the glyph is not a lucide
 *     icon.
 *
 *   <IconButton label="Close" icon={X} onClick={onHide} />
 *   <IconButton label="Remove file" icon={Trash2} variant="ghost-destructive" size="sm" />
 *
 * Props:
 *   label     — REQUIRED. The accessible name; also the tooltip.
 *   icon      — a component taking `size` (any lucide icon)
 *   size      — "xs" (22px) | "sm" (28px) | "md" (36px, default)
 *   variant   — any AppButton variant. Defaults to "ghost".
 *   showTitle — set false to suppress the tooltip while keeping the aria-label,
 *               for a button that already sits beside its own visible text.
 */
const ICON_BUTTON_SIZE_MAP = { xs: "icon-xs", sm: "icon-sm", md: "icon" };
const ICON_GLYPH_SIZE_MAP = { xs: 13, sm: 15, md: 17 };

const warnedMissingLabel = new Set();

const IconButton = React.forwardRef(
  (
    {
      label,
      icon: Icon,
      size = "md",
      variant = "ghost",
      showTitle = true,
      children,
      ...props
    },
    ref
  ) => {
    if (process.env.NODE_ENV !== "production" && !label && !warnedMissingLabel.has("label")) {
      warnedMissingLabel.add("label");
      // eslint-disable-next-line no-console
      console.warn(
        "[IconButton] `label` is required — an icon-only button with no accessible name announces as \"button\"."
      );
    }

    const buttonSize = ICON_BUTTON_SIZE_MAP[size] || ICON_BUTTON_SIZE_MAP.md;

    return (
      <AppButton
        ref={ref}
        variant={variant}
        size={buttonSize}
        aria-label={label}
        title={showTitle ? label : undefined}
        {...props}
      >
        {Icon ? (
          <Icon size={ICON_GLYPH_SIZE_MAP[size] || ICON_GLYPH_SIZE_MAP.md} aria-hidden="true" />
        ) : (
          children
        )}
      </AppButton>
    );
  }
);

IconButton.displayName = "IconButton";
export default IconButton;
